'use client';

import { usePersistentVoiceCallOptional } from '@/contexts/persistent-voice-call-context';
import { usePathname } from 'next/navigation';
import { getCanonicalThreadPath } from '@/lib/thread-routes';
import { Skeleton } from '@/components/ui/skeleton';
import { type ReactElement } from 'react';

function ChatTopFade(): ReactElement {
  return (
    <div className="workspace-canvas-fade-top pointer-events-none absolute inset-x-0 top-0 h-12" />
  );
}

function ChatBottomFade(): ReactElement {
  return (
    <div className="workspace-canvas-fade-bottom pointer-events-none absolute inset-x-0 bottom-0 h-16" />
  );
}

/**
 * Pure thread loading column (no hints, providers or queries), shared by the
 * route loading layout and the workspace admission shell.
 */
export function ThreadLoadingColumn({
  startupPending = false,
}: {
  /**
   * A just-sent thread is arriving with its startup handoff: leave the
   * transcript empty instead of flashing placeholder rows for a few frames
   * before the prompt renders.
   */
  startupPending?: boolean;
}) {
  const call = usePersistentVoiceCallOptional()?.call;
  const pathname = usePathname();
  const voiceStartup = Boolean(
    call && pathname === getCanonicalThreadPath(call.orgSlug, call.routeRef)
  );
  return (
    <div
      data-thread-loading-shell="messages"
      className="workspace-canvas-surface relative flex h-full min-h-0 overflow-hidden"
    >
      <div className="absolute top-0 right-0 left-0 z-50 px-2 py-1">
        <ChatTopFade />
        <div className="relative z-[100] flex items-center justify-between gap-1">
          <div className="flex items-center">
            <div className="h-8 w-8 lg:hidden" />
          </div>
          <div className="min-w-0 flex-1" />
          <div className="flex items-center gap-1">
            <div className="h-8 w-8" />
          </div>
        </div>
      </div>
      <div className="mx-auto min-h-0 w-full max-w-[540px] flex-1 space-y-6 overflow-y-auto px-4 pt-20 pb-32 sm:max-w-[440px] md:max-w-[500px] lg:max-w-[600px] xl:max-w-[700px]">
        {Array.from({ length: startupPending || voiceStartup ? 0 : 4 }).map(
          (_, i) => (
            <div key={i} className="space-y-3">
              <div className="flex items-start gap-3">
                <Skeleton className="h-8 w-8 flex-shrink-0 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-20" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                </div>
              </div>
            </div>
          )
        )}
      </div>

      {!voiceStartup && (
        <div className="absolute right-0 bottom-0 left-0 z-10 px-2 pt-8 pb-2 sm:px-4">
          <ChatBottomFade />
          <div className="relative mx-auto max-w-xl md:py-4">
            <Skeleton className="h-12 w-full rounded-lg" />
          </div>
        </div>
      )}
    </div>
  );
}
